import { useState, useEffect } from 'react';
import { useParams, useHistory, Link } from 'react-router-dom';
import { fetchData, updateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Alert, Snackbar, Dialog, Button, Chip, Tooltip } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SecurityIcon from '@mui/icons-material/Security';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import EditIcon from '@mui/icons-material/Edit';
import ClaimsManager from './ClaimsManager';
import RolesManager from './RolesManager';

const UserDetail = () => {
    useSessionValidation();

    const { id } = useParams();
    const { isAuthenticated, companyId, token, logout } = useAuth();

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) { logout(); }
    }, [isAuthenticated, companyId]);

    const [isSavingRoles, setIsSavingRoles] = useState(false);
    const [isSavingClaims, setIsSavingClaims] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [isClaimEditMode, setIsClaimEditMode] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const [formData, setFormData] = useState({
        FirstName: '', LastName: '', UserName: '',
        Email: '', ContactNumber: '', Roles: [],
    });

    const [formDataClaim, setFormDataClaim] = useState({ Claim: [] });

    useEffect(() => {
        fetchData('UserApi', `/UserEdit/${id}`, token)
            .then((response) => {
                if (response.responseCode === '1111') {
                    const { User } = response.response_data;
                    setFormData((prev) => ({
                        ...prev, ...User,
                        Roles: response.response_data.Roles?.map((r) => ({ ...r })) || [],
                    }));
                    setFormDataClaim((prev) => ({
                        ...prev, ...User,
                        Claim: response.response_data.Claims?.map((c) => ({ ...c })) || [],
                    }));
                }
            })
            .catch((error) => console.error('Error fetching User Details:', error));
    }, [id]);

    const saveRoles = async (updatedRoles) => {
        try {
            setIsSavingRoles(true);
            const updated = { ...formData, Roles: updatedRoles || formData.Roles };
            const response = await updateData('UserApi', `SaveUserRoles/tmp/${id}`, updated, token);
            if (response.responseCode === '1111') {
                setSuccessMessage('Roles saved successfully');
                setFormData(updated);
            } else { setErrorMessage(''); }
            setIsEditMode(false);
        } catch { setErrorMessage('Error saving roles'); }
        finally { setIsSavingRoles(false); }
    };

    const saveClaims = async (updatedClaims) => {
        try {
            setIsSavingClaims(true);
            const updated = { ...formDataClaim, Claim: updatedClaims || formDataClaim.Claim };
            const response = await updateData('UserApi', `SaveUserClaims/UpdateClaim/tmp/${id}`, updated, token);
            if (response.responseCode === '1111') {
                setSuccessMessage('Claims saved successfully');
                setFormDataClaim(updated);
            } else { setErrorMessage(''); }
            setIsClaimEditMode(false);
        } catch { setErrorMessage('Error saving claims'); }
        finally { setIsSavingClaims(false); }
    };

    const inputStyle = {
        width: '100%', padding: '6px 10px', fontSize: '13px',
        borderRadius: '8px', border: '1px solid #d0d7e3',
        background: '#f8f9fa', outline: 'none', fontFamily: 'inherit',
        cursor: 'not-allowed', color: '#4a5568', boxSizing: 'border-box'
    };

    const labelStyle = {
        display: 'block', fontSize: '11px', fontWeight: '600',
        color: '#4a5568', marginBottom: '4px',
        textTransform: 'uppercase', letterSpacing: '0.3px'
    };

    const sectionStyle = {
        border: '1px solid #e2e8f0', borderRadius: '10px',
        padding: '12px', marginBottom: '10px', background: '#fafafa'
    };

    const sectionTitleStyle = {
        display: 'flex', alignItems: 'center', gap: '6px',
        fontSize: '12px', fontWeight: '700', color: '#2C3E6E',
        textTransform: 'uppercase', letterSpacing: '0.5px',
        marginBottom: '10px', paddingBottom: '6px',
        borderBottom: '1px solid #e2e8f0'
    };

    const statBoxStyle = (bg) => ({
        background: bg, padding: '6px 10px', borderRadius: '6px',
        color: 'white', display: 'flex', alignItems: 'center',
        justifyContent: 'center', gap: '6px'
    });

    return (
        <div style={{ minWidth: '100%', background: '#f5f7fa', padding: '10px', position: 'relative' }}>
            <div style={{ maxWidth: '1400px', margin: '0 auto', background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: '12px', color: 'black', fontFamily: 'Times New Roman' }}>

                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', paddingBottom: '8px', borderBottom: '1px solid #f0f0f0' }}>
                    <h4 style={{ fontSize: '17px', fontWeight: '700', margin: 0, fontFamily: 'Times New Roman', textTransform: 'uppercase', color: 'black' }}>
                        User Details
                    </h4>
                    <Button
                        variant="outlined"
                        component={Link}
                        to="/UserIndex"
                        startIcon={<ArrowBackIcon sx={{ fontSize: '16px' }} />}
                        sx={{
                            borderColor: '#2C3E6E', color: '#2C3E6E', padding: '4px 12px',
                            borderRadius: '6px', fontSize: '12px', fontWeight: '700',
                            minHeight: '29px', textTransform: 'none', whiteSpace: 'nowrap'
                        }}
                    >
                        Back
                    </Button>
                </div>

                {/* Personal Information */}
                <div style={sectionStyle}>
                    <div style={sectionTitleStyle}>
                        <PersonIcon style={{ fontSize: '14px', color: '#2C3E6E' }} />
                        Personal Information
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
                        {[
                            { label: 'First Name', value: formData.FirstName, icon: <PersonIcon style={{ fontSize: '13px' }} /> },
                            { label: 'Last Name', value: formData.LastName, icon: <PersonIcon style={{ fontSize: '13px' }} /> },
                            { label: 'User Name', value: formData.UserName, icon: <AccountCircleIcon style={{ fontSize: '13px' }} /> },
                            { label: 'Contact Number', value: formData.ContactNumber, icon: <PhoneIcon style={{ fontSize: '13px' }} /> },
                            { label: 'Email Address', value: formData.Email, icon: <EmailIcon style={{ fontSize: '13px' }} /> },
                        ].map(({ label, value, icon }) => (
                            <div key={label}>
                                <label style={labelStyle}>{label}</label>
                                <input value={value || ''} type="text" readOnly style={inputStyle} />
                            </div>
                        ))}
                    </div>
                </div>

                {/* Roles & Claims */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: '10px' }}>

                    {/* Claims */}
                    <div style={sectionStyle}>
                        <div style={{ ...sectionTitleStyle, justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <SecurityIcon style={{ fontSize: '14px', color: '#2C3E6E' }} />
                                User Claims
                            </div>
                            <Tooltip title="Manage user claims" arrow>
                                <Button
                                    onClick={() => setIsClaimEditMode(true)}
                                    variant="contained"
                                    size="small"
                                    startIcon={<EditIcon sx={{ fontSize: '14px' }} />}
                                    sx={{
                                        background: '#2C3E6E', color: 'white', padding: '2px 10px',
                                        borderRadius: '6px', fontSize: '11px', fontWeight: '700',
                                        minHeight: '24px', textTransform: 'none'
                                    }}
                                >
                                    Manage
                                </Button>
                            </Tooltip>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', marginBottom: '10px' }}>
                            <div style={statBoxStyle('#2C3E6E')}>
                                <div style={{ fontSize: '18px', fontWeight: '700' }}>{formDataClaim.Claim?.length || 0}</div>
                                <div style={{ fontSize: '10px', opacity: 0.9 }}>Total Claims</div>
                            </div>
                            <div style={statBoxStyle('linear-gradient(135deg, #11998e 0%, #38ef7d 100%)')}>
                                <div style={{ fontSize: '18px', fontWeight: '700' }}>{formDataClaim.Claim?.filter(c => c.IsSelected).length || 0}</div>
                                <div style={{ fontSize: '10px', opacity: 0.9 }}>Active Claims</div>
                            </div>
                        </div>
                        <div style={{ maxHeight: '200px', overflowY: 'auto', background: 'white', borderRadius: '8px', padding: '8px' }}>
                            <div style={{ fontSize: '11px', fontWeight: '600', color: '#4a5568', marginBottom: '6px' }}>Active Claims:</div>
                            {formDataClaim.Claim?.filter(c => c.IsSelected).length > 0 ? (
                                formDataClaim.Claim.filter(c => c.IsSelected).map((claim, i) => (
                                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '5px 8px', marginBottom: '4px', background: 'rgba(44,62,110,0.07)', borderRadius: '4px', border: '1px solid #2C3E6E' }}>
                                        <CheckCircleIcon style={{ fontSize: '13px', color: '#2C3E6E' }} />
                                        <span style={{ fontSize: '11px', fontWeight: '500', color: '#2C3E6E' }}>{claim.ClaimValue}</span>
                                    </div>
                                ))
                            ) : (
                                <p style={{ fontSize: '11px', color: '#666', textAlign: 'center', padding: '15px', margin: 0 }}>No active claims. Click "Manage" to assign permissions.</p>
                            )}
                        </div>
                    </div>

                    {/* Roles */}
                    <div style={sectionStyle}>
                        <div style={{ ...sectionTitleStyle, justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <AdminPanelSettingsIcon style={{ fontSize: '14px', color: '#2C3E6E' }} />
                                User Roles
                            </div>
                            <Tooltip title="Manage user roles" arrow>
                                <Button
                                    onClick={() => setIsEditMode(true)}
                                    variant="contained"
                                    size="small"
                                    startIcon={<EditIcon sx={{ fontSize: '14px' }} />}
                                    sx={{
                                        background: '#2C3E6E', color: 'white', padding: '2px 10px',
                                        borderRadius: '6px', fontSize: '11px', fontWeight: '700',
                                        minHeight: '24px', textTransform: 'none'
                                    }}
                                >
                                    Manage
                                </Button>
                            </Tooltip>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', marginBottom: '10px' }}>
                            <div style={statBoxStyle('#2C3E6E')}>
                                <div style={{ fontSize: '18px', fontWeight: '700' }}>{formData.Roles?.length || 0}</div>
                                <div style={{ fontSize: '10px', opacity: 0.9 }}>Total Roles</div>
                            </div>
                            <div style={statBoxStyle('linear-gradient(135deg, #11998e 0%, #38ef7d 100%)')}>
                                <div style={{ fontSize: '18px', fontWeight: '700' }}>{formData.Roles?.filter(r => r.IsSelected).length || 0}</div>
                                <div style={{ fontSize: '10px', opacity: 0.9 }}>Active Role</div>
                            </div>
                        </div>
                        <div style={{ maxHeight: '200px', overflowY: 'auto', background: 'white', borderRadius: '8px', padding: '8px' }}>
                            <div style={{ fontSize: '11px', fontWeight: '600', color: '#4a5568', marginBottom: '6px' }}>Active Role:</div>
                            {formData.Roles?.filter(r => r.IsSelected).length > 0 ? (
                                formData.Roles.filter(r => r.IsSelected).map((role, i) => (
                                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '5px 8px', marginBottom: '4px', background: 'rgba(44,62,110,0.07)', borderRadius: '4px', border: '1px solid #2C3E6E' }}>
                                        <CheckCircleIcon style={{ fontSize: '13px', color: '#2C3E6E' }} />
                                        <span style={{ fontSize: '11px', fontWeight: '500', color: '#2C3E6E' }}>{role.RoleName}</span>
                                    </div>
                                ))
                            ) : (
                                <p style={{ fontSize: '11px', color: '#666', textAlign: 'center', padding: '15px', margin: 0 }}>No active role. Click "Manage" to assign a role.</p>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Claims Dialog */}
            <Dialog open={isClaimEditMode} onClose={() => setIsClaimEditMode(false)} maxWidth="md" fullWidth PaperProps={{ style: { borderRadius: '16px', maxHeight: '90vh' } }}>
                <ClaimsManager claims={formDataClaim.Claim || []} onSave={saveClaims} onCancel={() => setIsClaimEditMode(false)} isSaving={isSavingClaims} />
            </Dialog>

            {/* Roles Dialog */}
            <Dialog open={isEditMode} onClose={() => setIsEditMode(false)} maxWidth="md" fullWidth PaperProps={{ style: { borderRadius: '16px', maxHeight: '90vh' } }}>
                <RolesManager roles={formData.Roles || []} onSave={saveRoles} onCancel={() => setIsEditMode(false)} isSaving={isSavingRoles} />
            </Dialog>

            <Snackbar open={!!errorMessage || !!successMessage} autoHideDuration={4000} onClose={() => { setErrorMessage(''); setSuccessMessage(''); }} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
                <Alert onClose={() => { setErrorMessage(''); setSuccessMessage(''); }} severity={errorMessage ? 'error' : 'success'}>
                    {errorMessage || successMessage}
                </Alert>
            </Snackbar>
        </div>
    );
};

export default UserDetail;

